import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { formatApiErrorDetail } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PaymentStatus = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Checking your payment status...');

  useEffect(() => {
    const gateway = searchParams.get('gateway');
    const merchantOrderId = searchParams.get('merchantOrderId');

    if (gateway !== 'phonepe' || !merchantOrderId) {
      setStatus('failed');
      setMessage('Payment details were missing from the return URL.');
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let timeoutId;

    const checkStatus = async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/orders/phonepe/${merchantOrderId}/status`,
          { withCredentials: true }
        );

        if (cancelled) return;

        if (data.status === 'completed') {
          navigate('/profile', {
            replace: true,
            state: {
              paymentMessage: 'PhonePe payment successful! Your order has been placed.'
            }
          });
          return;
        }

        if (data.status === 'failed') {
          setStatus('failed');
          setMessage(data.error_message || 'PhonePe payment failed. Please try again.');
          return;
        }

        attempts += 1;
        setStatus('pending');
        setMessage('Waiting for PhonePe to confirm your payment...');

        if (attempts < 5) {
          timeoutId = window.setTimeout(checkStatus, 3000);
        }
      } catch (error) {
        if (cancelled) return;
        setStatus('failed');
        setMessage(formatApiErrorDetail(error.response?.data?.detail) || 'Unable to verify payment status.');
      }
    };

    checkStatus();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [navigate, searchParams]);

  return (
    <div className="heritage-page min-h-screen flex flex-col">
      <Header />

      <main className="heritage-shell max-w-3xl flex-1">
        <div className="heritage-card rounded-sm p-8 text-center">
          <h1 className="heritage-title mb-4 text-4xl">Payment Status</h1>
          <p className="text-[#4d3a31]">{message}</p>

          {status === 'pending' && (
            <div className="mt-6 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#c79a35]"></div>
            </div>
          )}

          {status !== 'loading' && (
            <div className="mt-8 flex justify-center gap-4">
              <Button
                type="button"
                onClick={() => navigate('/profile')}
                className="heritage-button rounded-none"
              >
                View Orders
              </Button>
              <Button
                type="button"
                variant="outline"
                className="rounded-none border-[#dcc9ad] bg-[rgba(255,251,243,0.78)] hover:bg-[#f3e7d2]"
                onClick={() => navigate('/checkout')}
              >
                Back to Checkout
              </Button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentStatus;
